import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { BrandProvider, useBrand } from '../../context/BrandContext';
import { CartProvider } from '../../context/CartContext';
import BrandHeader from './BrandHeader';
import BrandMenuCard from './BrandMenuCard';
import BrandEnquiryCart from './BrandEnquiryCart';
import './BrandSite.css';

const BrandContent = () => {
  const { brand, loading, notFound } = useBrand();
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const { slug } = useParams();

  useEffect(() => {
    if (!slug) return;
    const fetchItems = async () => {
      try {
        const snap = await getDocs(collection(db, 'brands', slug, 'menuItems'));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setItems(all.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999)));
      } catch { setItems([]); }
      setItemsLoading(false);
    };
    fetchItems();
  }, [slug]);

  if (loading) return <div className="brand-loading"><div className="brand-loading-spinner" /></div>;
  if (notFound) return (
    <div className="brand-not-found">
      <h2>Brand not found</h2>
      <p>This catering page doesn't exist yet.</p>
      <a href="/" className="btn-primary">← Back to CateringCo</a>
    </div>
  );

  const filtered = activeCategory === 'All' ? items : items.filter(i => i.category === activeCategory);
  const categories = ['All', ...new Set(items.map(i => i.category).filter(Boolean))];

  return (
    <div className="brand-site" style={{ '--brand-primary': brand.primaryColor || '#111', '--brand-secondary': brand.secondaryColor || '#fff' }}>
      <BrandHeader brand={brand} />

      {/* Hero */}
      <section className="brand-hero" style={{ background: brand.primaryColor || '#111' }}>
        <div className="brand-hero-pattern" />
        <div className="container brand-hero-content">
          <div className="brand-hero-eyebrow">
            <div className="brand-hero-line" />
            <span>Catering Enquiry</span>
          </div>
          <h1 className="brand-hero-title">{brand.name?.toUpperCase() || 'OUR BRAND'}</h1>
          <span className="brand-hero-outline">CATERING.</span>
          <p className="brand-hero-tagline-display">{brand.tagline || 'Fresh · Gourmet · Made to Order'}</p>
          <div className="brand-hero-divider" />
          <p className="brand-hero-sub">Browse the menu, add items to your enquiry, select your store and submit. We'll be in touch!</p>
          <div className="brand-hero-steps">
            {['Browse the menu', 'Add to enquiry', 'Select store & submit'].map((s, i) => (
              <div key={i} className="brand-hero-step">
                <span className="brand-hero-step-num">0{i+1}</span>{s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="brand-menu">
        <div className="container">
          <div className="brand-menu-header">
            <p className="brand-menu-count">{items.length} items available</p>
            <h2 className="brand-menu-title">THE MENU</h2>
          </div>
          <div className="brand-category-nav">
            {categories.map(cat => (
              <button key={cat} className={`brand-cat-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
                style={activeCategory === cat ? { background: brand.primaryColor || '#111', borderColor: brand.primaryColor || '#111' } : {}}>
                {cat}
              </button>
            ))}
          </div>
          {itemsLoading ? (
            <div className="brand-menu-grid">
              {[1,2,3,4,5,6].map(i => <div key={i} className="brand-skeleton" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="brand-empty"><h3>NO ITEMS YET</h3><p>Menu coming soon.</p></div>
          ) : (
            <div className="brand-menu-grid">
              {filtered.map(item => <BrandMenuCard key={item.id} item={item} brand={brand} />)}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="brand-footer" style={{ background: brand.primaryColor || '#111' }}>
        <div className="container brand-footer-inner">
          <div className="brand-footer-left">
            {brand.logoUrl && <img src={brand.logoUrl} alt={brand.name} className="brand-footer-logo" />}
            <div>
              <div className="brand-footer-name">{brand.name}</div>
              <div className="brand-footer-tag">{brand.tagline}</div>
            </div>
          </div>
          <div className="brand-footer-right">
            <p className="brand-footer-copy">© {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
            <a href="/" className="brand-footer-platform">Powered by CateringCo</a>
          </div>
        </div>
      </footer>

      <BrandEnquiryCart brand={brand} slug={slug} />
    </div>
  );
};

const BrandSite = () => {
  const { slug } = useParams();
  return (
    <BrandProvider slug={slug}>
      <CartProvider>
        <BrandContent />
      </CartProvider>
    </BrandProvider>
  );
};

export default BrandSite;
